import { useState } from 'react';
import { API_BASE_URL, type InviteSignIn } from '@/lib/api';
import {
  INVITE_SIGN_IN_COPY, existingAccountLine, googleInviteUrl, stashInviteReturn, type InviteReturnKind,
} from '@/lib/inviteSignIn';
import { fillInviteCopy } from '@/lib/inviteLink';
import formStyles from '@/components/AuthForm.module.css';

/**
 * The sign-in an invite page offers when the invite's email already has an account (Warren, 29 October 2026): "You
 * already have an EV Initiative account with this email. Sign in to add <role> to it." Its password, a one-time
 * sign-in link to its own mailbox (which comes back to this page), or Google (which comes back here too). The page
 * accepts with what this hands it; the Api decides (lib/inviteSignIn.ts there). Never "email taken".
 */
export function InviteSignInPanel({ kind, token, email, roleLabel, busy, error, onAccept, onRequestLink }: {
  kind: InviteReturnKind;
  token: string;
  email: string;
  roleLabel: string;
  busy: boolean;
  error: string;
  onAccept: (signIn: InviteSignIn) => Promise<void>;
  onRequestLink: () => Promise<void>;
}) {
  const [password, setPassword] = useState('');
  const [link, setLink] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const c = INVITE_SIGN_IN_COPY;

  const sendLink = async (): Promise<void> => {
    setLink('sending');
    try { await onRequestLink(); setLink('sent'); } catch { setLink('error'); }
  };

  return (
    <div className={formStyles.form}>
      <p>{existingAccountLine(roleLabel)}</p>
      <form
        className={formStyles.form}
        noValidate
        onSubmit={(e) => { e.preventDefault(); if (password && !busy) void onAccept({ password }); }}
      >
        <div className={formStyles.field}>
          <label className={formStyles.label} htmlFor="invite-sign-in-email">{c.email}</label>
          <input id="invite-sign-in-email" className={formStyles.input} type="email" value={email} readOnly autoComplete="username" />
        </div>
        <div className={formStyles.field}>
          <label className={formStyles.label} htmlFor="invite-sign-in-password">{c.password}</label>
          <input
            id="invite-sign-in-password"
            className={formStyles.input}
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        {error && <div className={formStyles.serverError} role="alert">{error}</div>}
        <button type="submit" className={formStyles.submitBtn} disabled={busy || !password}>
          {busy ? c.signingIn : c.signIn}
        </button>
      </form>
      <p>{c.orLine}</p>
      {link === 'sent' ? (
        <p role="status">{fillInviteCopy(c.linkSent, { email })}</p>
      ) : (
        <button type="button" className={formStyles.btnSecondary} disabled={busy || link === 'sending'} onClick={() => { void sendLink(); }}>
          {link === 'sending' ? c.sendingLink : c.emailLink}
        </button>
      )}
      {link === 'error' && <div className={formStyles.serverError} role="alert">{c.linkFailed}</div>}
      <button
        type="button"
        className={formStyles.btnSecondary}
        disabled={busy}
        onClick={() => { stashInviteReturn(kind, token); window.location.assign(googleInviteUrl(API_BASE_URL, kind)); }}
      >
        {c.google}
      </button>
    </div>
  );
}

export default InviteSignInPanel;
